// Engine layer (PLAY01, PLAY04): capability probe, engine choice by capability tests, one uniform handle over
// hls.js, native HLS, mpegts.js and progressive files. hls.js and mpegts.js are lazy-loaded.
// ADR-0006: a 'direct' session's playbackUrl is the provider's cross-origin token URL. Every engine loads it without
// credentials (fetch 'omit', XHR withCredentials false, <video> without crossorigin: nothing reads its pixels) and
// no referrer; provider 401/403 there means an expired or refused token, which the controller renews.
import type { ClientCapabilities, PlaybackSession } from '@tuned/contracts';
import { attachConverted, conversionSupport, decide, probeSource, type Decision } from './convert/index.ts';

export type EngineName = 'hlsjs' | 'native' | 'mpegts' | 'progressive' | 'convert';
export type { Decision };
/** `lang`: the track's language tag as the stream declares it (ISO 639-1/-2 or BCP 47), when it has one. */
export type Track = { id: number; label: string; lang?: string };
/** A fatal engine problem. `code` set = not retryable; otherwise the controller runs bounded recovery. */
export type EngineFault = {
  kind: 'network' | 'media';
  code?: 'SESSION_EXPIRED' | 'RESOURCE_FORBIDDEN' | 'UNSUPPORTED_MEDIA' | 'CONVERSION_FAILED';
  reason: string;
  httpStatus?: number;
};
/** onChange: tracks, levels or transport info changed. */
export type EngineHooks = { onFatal(fault: EngineFault): void; onChange(): void };
export type Transport = 'downgraded' | null;
export type EngineHandle = {
  name: EngineName;
  destroy(): void;
  /** Quality levels; -1 = Auto. Empty when the engine cannot choose. */
  levels(): Track[];
  level(): number;
  setLevel(id: number): void;
  audioTracks(): Track[];
  audioTrack(): number;
  setAudioTrack(id: number): void;
  /** Subtitles/captions; -1 = off. */
  subtitleTracks(): Track[];
  subtitleTrack(): number;
  setSubtitleTrack(id: number): void;
  /** Seekable window for live; `end` is where "Go live" lands. Null when nothing is seekable. */
  seekWindow(): { start: number; end: number } | null;
  /** 'downgraded' when the relay reports `x-tuned-transport: downgraded` (provider redirected https→http). */
  transport(): Transport;
  /** Client-side conversion only (ADR-0004): every seek restarts the pipeline at `sec`. */
  seek?(sec: number): Promise<void>;
  /** Progressive only: the native-vs-convert decision this attach used. */
  decision?: Decision;
  /** hls.js only: first network recovery restarts loading instead of recreating the engine. */
  recoverNetwork?(): void;
  /** hls.js only: attempt 1 = recoverMediaError, attempt 2 = swapAudioCodec + recoverMediaError. */
  recoverMedia?(attempt: number): void;
};

// PLAY04 per-tile budgets (120 s live buffer ceiling, 64 MiB).
export const HLS_CONFIG = {
  maxBufferLength: 30, maxMaxBufferLength: 120, backBufferLength: 60, maxBufferSize: 64 * 1024 * 1024,
  liveDurationInfinity: true, enableWorker: true,
};
export const MPEGTS_CONFIG = {
  // Chase the live edge but keep a few seconds of cushion (providers burst on connect; jitter on real networks).
  enableWorker: true, enableStashBuffer: false, liveBufferLatencyChasing: true, liveBufferLatencyMaxLatency: 8, liveBufferLatencyMinRemain: 4,
  autoCleanupSourceBuffer: true,
  autoCleanupMaxBackwardDuration: 60, autoCleanupMinBackwardDuration: 30, lazyLoad: false,
  referrerPolicy: 'no-referrer' as ReferrerPolicy, // mpegts.js defaults to no-referrer-when-downgrade, overriding the page policy
};
/** hls.js for a cross-origin provider URL: its XHR loader never sends cookies or other credentials. */
const noCredentials = (xhr: XMLHttpRequest) => { xhr.withCredentials = false; };

const VIDEO_CODECS = ['avc1.42e01e', 'avc1.4d401e', 'avc1.64001f', 'hvc1.1.6.L93.B0', 'hev1.1.6.L93.B0'];
const AUDIO_CODECS = ['mp4a.40.2', 'mp4a.40.5', 'mp3', 'ac-3', 'ec-3', 'opus'];
const CONTAINERS = ['video/mp4', 'video/webm', 'video/x-matroska', 'video/x-msvideo', 'video/mp2t', 'video/quicktime', 'application/vnd.apple.mpegurl'];
const MPEGURL = 'application/vnd.apple.mpegurl';

type TypeProbe = { isTypeSupported?(type: string): boolean };
const mediaSources = () => {
  const g = globalThis as unknown as { MediaSource?: TypeProbe; ManagedMediaSource?: TypeProbe };
  return [g.MediaSource, g.ManagedMediaSource].filter((m): m is TypeProbe => !!m);
};
export const mseTypeSupported = (type: string) => mediaSources().some((m) => !!m.isTypeSupported?.(type));
/** MIME type used to probe one codec: mp3 → audio/mpeg, audio codecs → audio/mp4, the rest → video/mp4. */
export function codecMime(codec: string): string {
  if (codec === 'mp3') return 'audio/mpeg';
  const audio = /^(mp4a|ac-3|ec-3|opus|flac)/i.test(codec);
  return `${audio ? 'audio' : 'video'}/mp4; codecs="${codec}"`;
}
const TS_STREAM_CODECS: Record<number, string> = { 0x1b: 'avc1.64001f', 0x24: 'hvc1.1.6.L93.B0', 0x0f: 'mp4a.40.2', 0x11: 'mp4a.40.2', 0x03: 'mp3', 0x04: 'mp3', 0x81: 'ac-3', 0x87: 'ec-3' };
/** Codec probe strings of the elementary streams declared in an MPEG-TS PMT ([] when the bytes are not clear TS). */
export function tsCodecs(b: Uint8Array): string[] {
  let pmtPid = -1;
  for (let i = 0; i + 188 <= b.length; i += 188) {
    if (b[i] !== 0x47) return [];
    const pid = ((b[i + 1] & 0x1f) << 8) | b[i + 2];
    if (!(b[i + 1] & 0x40) || (pid !== 0 && pid !== pmtPid)) continue;
    let o = i + 4 + ((b[i + 3] & 0x20) ? 1 + b[i + 4] : 0);
    o += 1 + b[o];                                            // pointer_field
    const end = Math.min(i + 188, o + 3 + (((b[o + 1] & 0x0f) << 8) | b[o + 2]) - 4);
    if (pid === 0) { if (b[o] === 0x00) pmtPid = ((b[o + 10] & 0x1f) << 8) | b[o + 11]; continue; }
    if (b[o] !== 0x02) continue;
    const out: string[] = [];
    for (let p = o + 12 + (((b[o + 10] & 0x0f) << 8) | b[o + 11]); p + 5 <= end;) {
      const type = b[p];
      const infoLen = ((b[p + 3] & 0x0f) << 8) | b[p + 4];
      let codec = TS_STREAM_CODECS[type];
      for (let d = p + 5; type === 0x06 && d + 2 <= p + 5 + infoLen; d += 2 + b[d + 1]) {
        if (b[d] === 0x6a) codec = 'ac-3';
        if (b[d] === 0x7a) codec = 'ec-3';
      }
      if (codec) out.push(codec);
      p += 5 + infoLen;
    }
    return out;
  }
  return [];
}
const undecodable = (codec: string): EngineFault => ({
  kind: 'media', code: 'UNSUPPORTED_MEDIA',
  reason: `This channel's ${/^(mp4a|mp3|ac-3|ec-3|opus)/.test(codec) ? 'audio' : 'video'} (${codec === 'ac-3' ? 'AC-3' : codec === 'ec-3' ? 'E-AC-3' : codec.startsWith('hvc1') ? 'HEVC' : codec}) cannot be decoded by this browser, and Tuned does not convert live streams. Try another channel or browser.`,
});
const canPlay = (v: HTMLVideoElement, type: string) => (v.canPlayType?.(type) ?? '') !== '';
// Mirrors mpegts.js getFeatureList().mseLivePlayback (H.264/AAC in MSE + streaming fetch) without loading it.
const mseLiveTs = () => mseTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"') && typeof ReadableStream !== 'undefined';

let probed: ClientCapabilities | null = null;
/** ClientCapabilities for POST /api/playback. Probed once per page load. */
export function probeCapabilities(): ClientCapabilities {
  if (probed) return probed;
  const v = document.createElement('video');
  const codecs: Record<string, boolean> = {};
  for (const c of [...VIDEO_CODECS, ...AUDIO_CODECS]) codecs[c] = mseTypeSupported(codecMime(c)) || canPlay(v, codecMime(c));
  for (const t of CONTAINERS) codecs[t] = canPlay(v, t);
  codecs['video/mp2t'] ||= mseLiveTs();
  // Chromium/Firefox decode H.264-in-Matroska through their WebM demuxer; progressive MKV is an attempt, not a promise.
  codecs['video/x-matroska'] ||= canPlay(v, 'video/webm; codecs="avc1.42E01E"');
  probed = {
    nativeHls: canPlay(v, MPEGURL),
    mse: mediaSources().length > 0,
    codecs,
    mobile: !!globalThis.matchMedia?.('(pointer: coarse)').matches && innerWidth < 1024,
    pictureInPicture: !!(document as { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled,
    convert: conversionSupport(),
  };
  return probed;
}
export const resetProbeForTests = () => { probed = null; };

export type StreamKind = 'hls' | 'ts' | 'file';
/** What the session carries: relay sessions say it in `delivery`, direct ones in the server's `engineHint` (ADR-0006). */
export function streamKind(s: Pick<PlaybackSession, 'delivery' | 'engineHint'>): StreamKind {
  if (s.delivery === 'direct') return s.engineHint === 'hlsjs' || s.engineHint === 'native' ? 'hls' : s.engineHint === 'mpegts' ? 'ts' : 'file';
  return s.delivery === 'hls_relay' ? 'hls' : s.delivery === 'ts_relay' ? 'ts' : 'file';
}
export type EngineEnv = { hlsJs: boolean; nativeHls: boolean; mseLive: boolean };
export type EngineChoice = { engine: EngineName } | { unsupported: string };
/** PLAY01: pick an engine from capability tests, never from file extensions or advertised formats. A server hint
 *  'native' wins when the browser has native HLS (the provider allows no CORS, so hls.js could not read it). */
export function chooseEngine(kind: StreamKind, env: EngineEnv, hint?: PlaybackSession['engineHint']): EngineChoice {
  if (kind === 'hls') {
    if (hint === 'native' && env.nativeHls) return { engine: 'native' };
    if (env.hlsJs) return { engine: 'hlsjs' };
    if (env.nativeHls) return { engine: 'native' };
    return { unsupported: 'This browser cannot play HLS: it has neither native HLS nor Media Source Extensions. Use a current Chrome, Edge, Firefox or Safari.' };
  }
  if (kind === 'ts') {
    if (env.mseLive) return { engine: 'mpegts' };
    return { unsupported: 'This live channel is raw MPEG-TS, which needs Media Source Extensions (Safari 17.1+ on iPhone). This browser does not have them.' };
  }
  return { engine: 'progressive' };
}

type RangeLike = { length: number; start(i: number): number; end(i: number): number };
const span = (r: RangeLike | undefined) => (r && r.length ? { start: r.start(0), end: r.end(r.length - 1) } : null);
const authFault = (status: number, what: string): EngineFault => ({
  kind: 'network', httpStatus: status,
  code: status === 401 ? 'SESSION_EXPIRED' : 'RESOURCE_FORBIDDEN',
  reason: `${what} was refused (HTTP ${status}).`,
});

type NativeAudioTrack = { label: string; language: string; enabled: boolean };
type NativeAudioList = ArrayLike<NativeAudioTrack> & EventTarget;
const nativeAudio = (v: HTMLVideoElement) => (v as unknown as { audioTracks?: NativeAudioList }).audioTracks;
const textTracks = (v: HTMLVideoElement) => Array.from(v.textTracks ?? []).filter((t) => t.kind === 'subtitles' || t.kind === 'captions');

/** Shared handle for engines that use the element's own tracks (native HLS, progressive, mpegts). */
function elementHandle(video: HTMLVideoElement, name: EngineName, destroy: () => void, transport: Transport = null): EngineHandle {
  return {
    name, destroy, transport: () => transport,
    levels: () => [], level: () => -1, setLevel() {},
    audioTracks: () => Array.from(nativeAudio(video) ?? []).map((t, id) => ({ id, label: t.label || t.language || `Track ${id + 1}`, lang: t.language || undefined })),
    audioTrack: () => Array.from(nativeAudio(video) ?? []).findIndex((t) => t.enabled),
    setAudioTrack: (id) => Array.from(nativeAudio(video) ?? []).forEach((t, i) => { t.enabled = i === id; }),
    subtitleTracks: () => textTracks(video).map((t, id) => ({ id, label: t.label || t.language || `Subtitles ${id + 1}`, lang: t.language || undefined })),
    subtitleTrack: () => textTracks(video).findIndex((t) => t.mode === 'showing'),
    setSubtitleTrack: (id) => textTracks(video).forEach((t, i) => { t.mode = i === id ? 'showing' : 'disabled'; }),
    seekWindow: () => span(video.seekable),
  };
}

const CONTAINER_NAMES: Record<string, string> = {
  'video/x-matroska': 'MKV', 'video/x-msvideo': 'AVI', 'video/webm': 'WebM', 'video/quicktime': 'MOV', 'video/mp4': 'MP4', 'video/mp2t': 'MPEG-TS',
};
/** User-facing UNSUPPORTED_MEDIA reason for a progressive file, from its container (session.mimeType). */
export function unsupportedFileReason(mimeType: string, errorCode: number | null): string {
  const type = mimeType.split(';')[0]!.trim().toLowerCase();
  const name = CONTAINER_NAMES[type];
  if (errorCode === 4 && name && name !== 'MP4') return `The ${name} container is not playable in this browser. Try Chrome or Edge, or ask your provider for an MP4 version.`;
  return `This file's audio or video codec can't be decoded by this browser${name ? ` (${name})` : ''}.`;
}

/**
 * Watches element tracks + element errors; classifies errors with a HEAD request: 401/403 → session/forbidden,
 * 5xx/unreachable → network (bounded reconnects). Otherwise decode (3) / source-not-supported (4) errors are
 * UNSUPPORTED_MEDIA for progressive files (never retried); for native HLS only 4 is.
 */
function watchElement(video: HTMLVideoElement, url: string, mimeType: string, name: 'native' | 'progressive', hooks: EngineHooks, fatal: (f: EngineFault) => void, refused: typeof authFault) {
  const onError = async () => {
    const err = video.error;
    if (!err) return;
    let status: number | undefined;
    try { status = (await fetch(url, { method: 'HEAD', credentials: credentialsFor(url) })).status; } catch { /* network, or no CORS */ }
    if (status === 401 || status === 403) return fatal(refused(status, name === 'native' ? 'The stream' : 'The file'));
    const reachable = status !== undefined && status < 500;
    if (reachable && (err.code === 4 || (err.code === 3 && name === 'progressive'))) {
      const reason = name === 'progressive'
        ? unsupportedFileReason(mimeType, err.code)
        : `This browser cannot decode this HLS stream${err.message ? ` (${err.message})` : ''}.`;
      return fatal({ kind: 'media', code: 'UNSUPPORTED_MEDIA', httpStatus: status, reason });
    }
    fatal({ kind: err.code === 3 ? 'media' : 'network', httpStatus: status, reason: err.message || `Media error ${err.code}` });
  };
  const lists = [video.textTracks as EventTarget | undefined, nativeAudio(video)].filter((l): l is EventTarget => !!l?.addEventListener);
  video.addEventListener('error', onError);
  for (const l of lists) { l.addEventListener('addtrack', hooks.onChange); l.addEventListener('removetrack', hooks.onChange); }
  return () => {
    video.removeEventListener('error', onError);
    for (const l of lists) { l.removeEventListener('addtrack', hooks.onChange); l.removeEventListener('removetrack', hooks.onChange); }
  };
}

const DOWNGRADED = 'x-tuned-transport';
/** Reads the relay's transport header from an XHR (hls.js default loader) or a fetch Response. */
function transportOf(details: unknown): Transport {
  const d = details as { getResponseHeader?(h: string): string | null; headers?: { get?(h: string): string | null } } | null;
  const v = d?.getResponseHeader?.(DOWNGRADED) ?? d?.headers?.get?.(DOWNGRADED);
  return v === 'downgraded' ? 'downgraded' : null;
}
const unsupported = (reason: string) => ({ fault: { kind: 'media', code: 'UNSUPPORTED_MEDIA', reason } as EngineFault });
/** Cookies only ever go to Tuned's own origin (the relay); a provider URL gets no credentials at all. */
export const credentialsFor = (url: string): RequestCredentials => (new URL(url, location.href).origin === location.origin ? 'same-origin' : 'omit');
/** A refused provider token (direct) is a network fault the controller recovers from by fetching a new token. */
const tokenRefused = (status: number, what: string): EngineFault => ({ kind: 'network', httpStatus: status, reason: `${what} was refused by the provider (HTTP ${status}).` });

const decisions = new Map<string, Decision>();   // per asset (PlayRequest.itemId), page lifetime (ADR-0004 §4)
export const clearDecisionsForTests = () => decisions.clear();
async function decisionFor(url: string, cacheKey: string | undefined): Promise<Decision> {
  const hit = cacheKey ? decisions.get(cacheKey) : undefined;
  if (hit) return hit;
  let d: Decision = { kind: 'native' };
  try { d = decide(await probeSource(url)); } catch { /* probe unavailable: attempt native; MediaError mapping covers failures */ }
  if (cacheKey) decisions.set(cacheKey, d);
  return d;
}
const isBuffered = (v: HTMLVideoElement, t: number) => Array.from({ length: v.buffered?.length ?? 0 }, (_, i) => i)
  .some((i) => t >= v.buffered.start(i) && t <= v.buffered.end(i));

/**
 * Attach the right engine for this session to `video`. Resolves to a handle, or `{ fault }` when playback cannot
 * start (UNSUPPORTED_MEDIA with a user-facing reason, 401/403, or a relay 5xx). Faults after attach arrive via
 * hooks.onFatal, once until recovered.
 */
export async function attachEngine(
  video: HTMLVideoElement,
  session: Pick<PlaybackSession, 'delivery' | 'engineHint' | 'playbackUrl' | 'mimeType' | 'capabilities'>,
  hooks: EngineHooks,
  opts: { startSec?: number; cacheKey?: string } = {},
): Promise<EngineHandle | { fault: EngineFault; decision?: Decision }> {
  const url = new URL(session.playbackUrl, location.href).href;
  const direct = session.delivery === 'direct';
  const refused = direct ? tokenRefused : authFault;
  const kind = streamKind(session);
  let armed = true;
  const fatal = (f: EngineFault) => { if (armed) { armed = false; hooks.onFatal(f); } };

  if (kind === 'hls') {
    const Hls = (await import('hls.js')).default;
    const choice = chooseEngine('hls', { hlsJs: Hls.isSupported(), nativeHls: canPlay(video, MPEGURL), mseLive: false }, session.engineHint);
    if ('unsupported' in choice) return unsupported(choice.unsupported);
    if (choice.engine === 'native') {
      video.src = url;
      return elementHandle(video, 'native', watchElement(video, url, session.mimeType, 'native', hooks, fatal, refused));
    }
    const hls = new Hls(direct ? { ...HLS_CONFIG, xhrSetup: noCredentials } : HLS_CONFIG);
    const codecErrors: string[] = [
      Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR, Hls.ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR, Hls.ErrorDetails.BUFFER_ADD_CODEC_ERROR,
    ];
    hls.on(Hls.Events.ERROR, (_ev, data) => {
      const status = data.response?.code;
      if (status === 401 || status === 403) return fatal(refused(status, 'The stream'));
      if (!data.fatal) return;
      if (codecErrors.includes(data.details)) {
        const detail = data.mimeType ?? data.reason ?? data.error?.message;
        return fatal({ kind: 'media', code: 'UNSUPPORTED_MEDIA', reason: `This stream uses audio or video codecs this browser cannot decode${detail ? ` (${detail})` : ''}.` });
      }
      fatal({ kind: data.type === Hls.ErrorTypes.NETWORK_ERROR ? 'network' : 'media', httpStatus: status || undefined, reason: `${data.details}${data.reason ? `: ${data.reason}` : ''}` });
    });
    for (const ev of [Hls.Events.MANIFEST_PARSED, Hls.Events.LEVELS_UPDATED, Hls.Events.LEVEL_SWITCHED, Hls.Events.AUDIO_TRACKS_UPDATED,
      Hls.Events.AUDIO_TRACK_SWITCHED, Hls.Events.SUBTITLE_TRACKS_UPDATED, Hls.Events.SUBTITLE_TRACK_SWITCH] as const) {
      hls.on(ev, () => hooks.onChange());
    }
    let transport: Transport = null;
    const checkTransport = (_ev: unknown, data: { networkDetails?: unknown }) => {
      if (!transport && transportOf(data.networkDetails)) { transport = 'downgraded'; hooks.onChange(); }
    };
    // hls.js silently skips a TS track MSE cannot take (e.g. AC-3 audio in Chrome) and plays the rest. That is a
    // silent failure, so read the first TS segment's PMT and surface UNSUPPORTED_MEDIA with the codec (PLAY01).
    let pmtChecked = false;
    hls.on(Hls.Events.FRAG_LOADED, (_ev, data) => {
      if (pmtChecked || !(data.payload instanceof ArrayBuffer)) return;
      pmtChecked = true;
      const bad = tsCodecs(new Uint8Array(data.payload)).find((c) => !mseTypeSupported(codecMime(c)));
      if (bad) fatal(undecodable(bad));
    });
    if (!direct) { // the relay's own header; a cross-origin XHR may not even read it
      hls.on(Hls.Events.MANIFEST_LOADED, checkTransport);
      hls.on(Hls.Events.LEVEL_LOADED, checkTransport);
    }
    hls.loadSource(url);
    hls.attachMedia(video);
    const label = (t: { name?: string; lang?: string }, i: number, kind: string) => t.name || t.lang || `${kind} ${i + 1}`;
    return {
      name: 'hlsjs',
      destroy: () => hls.destroy(),
      transport: () => transport,
      levels: () => hls.levels.map((l, id) => ({ id, label: l.height ? `${l.height}p` : `${Math.round(l.bitrate / 1000)} kbps` })),
      level: () => (hls.autoLevelEnabled ? -1 : hls.currentLevel),
      setLevel: (id) => { hls.nextLevel = id; },
      audioTracks: () => hls.audioTracks.map((t, id) => ({ id, label: label(t, id, 'Track'), lang: t.lang || undefined })),
      audioTrack: () => hls.audioTrack,
      setAudioTrack: (id) => { hls.audioTrack = id; },
      subtitleTracks: () => hls.subtitleTracks.map((t, id) => ({ id, label: label(t, id, 'Subtitles'), lang: t.lang || undefined })),
      subtitleTrack: () => (hls.subtitleDisplay ? hls.subtitleTrack : -1),
      setSubtitleTrack: (id) => { hls.subtitleTrack = id; hls.subtitleDisplay = id >= 0; },
      seekWindow: () => {
        const w = span(video.seekable);
        const sync = hls.liveSyncPosition;
        return w && sync !== null && sync > w.start && sync < w.end ? { start: w.start, end: sync } : w;
      },
      recoverNetwork: () => { armed = true; hls.startLoad(); },
      recoverMedia: (attempt) => { armed = true; if (attempt > 1) hls.swapAudioCodec(); hls.recoverMediaError(); },
    };
  }

  if (kind === 'ts') {
    const mpegts = (await import('mpegts.js')).default;
    const choice = chooseEngine('ts', { hlsJs: false, nativeHls: false, mseLive: mpegts.getFeatureList().mseLivePlayback });
    if ('unsupported' in choice) return unsupported(choice.unsupported);
    const player = mpegts.createPlayer({ type: 'mpegts', isLive: session.capabilities.live, url }, MPEGTS_CONFIG);
    const { ErrorTypes, ErrorDetails } = mpegts;
    const codecErrors = [ErrorDetails.MEDIA_MSE_ERROR, ErrorDetails.MEDIA_CODEC_UNSUPPORTED, ErrorDetails.MEDIA_FORMAT_UNSUPPORTED];
    player.on(mpegts.Events.ERROR, (type: string, detail: string, info?: { code?: number; msg?: string }) => {
      const status = typeof info?.code === 'number' && info.code >= 100 ? info.code : undefined;
      if (type === ErrorTypes.NETWORK_ERROR) {
        if (status === 401 || status === 403) return fatal(refused(status, 'The stream'));
        return fatal({ kind: 'network', httpStatus: status, reason: `${detail}${info?.msg ? `: ${info.msg}` : ''}` });
      }
      if (type === ErrorTypes.MEDIA_ERROR && codecErrors.includes(detail)) {
        return fatal({ kind: 'media', code: 'UNSUPPORTED_MEDIA', reason: `This browser cannot decode this channel${info?.msg ? ` (${info.msg})` : ''}.` });
      }
      fatal({ kind: 'media', reason: `${detail}${info?.msg ? `: ${info.msg}` : ''}` });
    });
    player.on(mpegts.Events.MEDIA_INFO, (mi: { audioCodec?: string; videoCodec?: string }) => {
      const bad = [mi.videoCodec, mi.audioCodec].find((c) => c && !mseTypeSupported(codecMime(c)));
      if (bad) fatal({ kind: 'media', code: 'UNSUPPORTED_MEDIA', reason: `This channel uses the ${bad} codec, which this browser cannot decode.` });
    });
    player.attachMediaElement(video);
    player.load();
    return {
      ...elementHandle(video, 'mpegts', () => { try { player.destroy(); } catch { /* already torn down */ } }),
      seekWindow: () => span(video.buffered), // mpegts.js cannot refetch the past: only the buffer is seekable
    };
  }

  // Progressive (relay): one 1-byte preflight classifies 401/403/415/5xx before the element hides the status, and
  // reads the transport header. Network failure here is left to the element's own error path. Direct sessions skip
  // it: the server just made the same check against the provider.
  let transport: Transport = null;
  if (!direct) {
    try {
      const pre = await fetch(url, { headers: { Range: 'bytes=0-0' }, credentials: 'same-origin' });
      transport = transportOf(pre);
      if (pre.status === 415) {
        const body = (await pre.json().catch(() => null)) as { error?: { details?: { reason?: unknown } } } | null;
        const reason = body?.error?.details?.reason;
        return unsupported(typeof reason === 'string' ? reason : unsupportedFileReason(session.mimeType, 4));
      }
      void pre.body?.cancel().catch(() => {});
      if (pre.status === 401 || pre.status === 403) return { fault: authFault(pre.status, 'The file') };
      if (pre.status >= 500) return { fault: { kind: 'network', httpStatus: pre.status, reason: `The relay answered HTTP ${pre.status}.` } };
    } catch { /* offline or aborted: the element reports it */ }
  }

  // ADR-0004: play natively when the browser can, else convert audio/remux in the browser, else a specific reason.
  const decision = await decisionFor(url, opts.cacheKey);
  if (decision.kind === 'unsupported') return { ...unsupported(decision.reason), decision };
  if (decision.kind === 'convert') {
    const ac = new AbortController();
    let conv: Awaited<ReturnType<typeof attachConverted>>;
    try {
      conv = await attachConverted(video, url, {
        startSec: opts.startSec, signal: ac.signal,
        onError: (e) => fatal({ kind: 'media', code: e.code, reason: e.reason }),
      });
    } catch (e) {
      ac.abort();
      return { fault: { kind: 'media', code: 'CONVERSION_FAILED', reason: `The in-browser converter could not start (${e instanceof Error ? e.message : String(e)}).` }, decision };
    }
    let target = opts.startSec ?? 0;
    const seek = (sec: number) => { target = sec; return conv.seek(sec); };
    // Seeks from anywhere (keyboard, PiP, media keys) restart the pipeline unless the target is already buffered.
    const onSeeking = () => { const t = video.currentTime; if (Math.abs(t - target) > 0.5 && !isBuffered(video, t)) void seek(t).catch(() => {}); };
    video.addEventListener('seeking', onSeeking);
    return {
      ...elementHandle(video, 'convert', () => { video.removeEventListener('seeking', onSeeking); ac.abort(); conv.destroy(); }, transport),
      seek, decision,
    };
  }
  video.src = url;
  return { ...elementHandle(video, 'progressive', watchElement(video, url, session.mimeType, 'progressive', hooks, fatal, refused), transport), decision };
}
